// Exhaustive by construction: a `Permission` added without a matching key here
// is a compile error (Record<Permission, true> requires every key, and rejects
// unknown ones), so `ALL_PERMISSIONS` can never drift from the `Permission` union.
export const PERMISSION_REGISTRY = {
    "inventory.editCost": true,
    "inventory.movements.receive": true,
    "inventory.movements.transfer": true,
    "inventory.movements.consume": true,
    "inventory.movements.adjust": true,
    "inventory.stock.read": true,
    "inventory.locations.manage": true,
    "inventory.lots.manage": true,
    "inventory.dailyLots.manage": true,
    "inventory.transactions.read": true,
    "inventory.trace.read": true,
    "administrations.manage": true,
    "users.manage": true,
    "roles.manage": true,
    "accounts.read": true,
    "accounts.create": true,
    "accounts.update": true,
    "accounts.delete": true,
    "accounts.pricing.manage": true,
    "orders.read": true,
    "orders.create": true,
    "orders.update": true,
    "orders.delete": true,
    "orders.convert": true,
    "orders.return": true,
    "orderItems.read": true,
    "kitchenChits.read": true,
    "kitchenChits.manage": true,
    "quotes.read": true,
    "quotes.manage": true,
    "invoices.read": true,
    "invoices.create": true,
    "invoices.update": true,
    "invoices.send": true,
    "invoices.delete": true,
    "invoices.creditNote.create": true,
    "payments.read": true,
    "payments.create": true,
    "payments.update": true,
    "payments.allocate": true,
    "payments.delete": true,
    "priceLists.read": true,
    "priceLists.manage": true,
    "priceBooks.read": true,
    "priceBooks.manage": true,
    "reservations.read": true,
    "reservations.manage": true,
    "items.read": true,
    "items.create": true,
    "items.update": true,
    "items.delete": true,
    "items.editCost": true,
    "categories.read": true,
    "categories.manage": true,
    "subcategories.read": true,
    "subcategories.manage": true,
    "labels.read": true,
    "labels.manage": true,
    "labels.printers.manage": true,
    "packages.read": true,
    "packages.manage": true,
    "suppliers.read": true,
    "suppliers.manage": true,
    "purchaseOrders.read": true,
    "purchaseOrders.create": true,
    "purchaseOrders.update": true,
    "purchaseOrders.receive": true,
    "purchaseOrders.approve": true,
    "purchaseInvoices.manage": true,
    "goodsReceipts.read": true,
    "goodsReceipts.manage": true,
    "goodsReceipts.reverse": true,
    "goodsReceipts.settings.manage": true,
    "supplierCatalog.read": true,
    "supplierCatalog.manage": true,
    "accounting.chartOfAccounts.manage": true,
    "accounting.journals.read": true,
    "accounting.journals.post": true,
    "accounting.gl.read": true,
    "accounting.trialBalance.read": true,
    "accounting.balances.read": true,
    "bankReconciliation.manage": true,
    "cashFlow.manage": true,
    "exchangeRates.manage": true,
    "expenses.manage": true,
    "fiscalYear.manage": true,
    "outstandingDebtors.read": true,
    "invoiceAging.read": true,
    "reports.read": true,
    "reports.schedules.manage": true,
    "planning.manage": true,
    "planning.recurring.manage": true,
    "routes.read": true,
    "routes.manage": true,
    "vehicles.read": true,
    "vehicles.manage": true,
    "fleet.tracking.read": true,
    "fleet.tracking.history": true,
    "trailers.read": true,
    "trailers.manage": true,
    "picking.read": true,
    "picking.manage": true,
    "picking.generate": true,
    "picking.rules.manage": true,
    "production.read": true,
    "production.manage": true,
    "employees.read": true,
    "employees.create": true,
    "employees.update": true,
    "employees.delete": true,
    "employees.absences.manage": true,
    "employees.availability.manage": true,
    "employees.hours.read": true,
    "employees.hours.manage": true,
    "employees.hours.approve": true,
    "employees.contracts.manage": true,
    "employees.costs.read": true,
    "pos.sessions.read": true,
    "pos.sessions.open": true,
    "pos.sessions.close": true,
    "pos.cashMovements.read": true,
    "pos.cashMovements.create": true,
    "pos.layout.manage": true,
    "pos.settings.manage": true,
    "tables.read": true,
    "tables.manage": true,
    "tables.bulkImport.manage": true,
    "tables.nfcTags.manage": true,
    "chat.use": true,
    "chat.channels.manage": true,
    "i18nAdmin.manage": true,
    "notifications.manage": true,
    "tenantSettings.manage": true,
    "apiKeys.manage": true,
    "security.read": true,
    "security.manage": true,
    "auditLog.read": true,
    "statusDefinitions.read": true,
    "statusDefinitions.manage": true,
    "pdfTemplates.manage": true,
    "automation.manage": true,
    "analytics.read": true,
    "dashboard.read": true,
    "integrations.mollie.manage": true,
    "integrations.snelstart.manage": true,
    "integrations.whatsapp.manage": true,
    "integrations.voice.manage": true,
    "integrations.twilio.manage": true,
    "integrations.routevision.manage": true,
    "integrations.email.manage": true,
    "integrations.kvk.read": true,
    "integrations.ai.manage": true,
    "bonnie.use": true,
    "bonnie.manage": true,
    "tenants.manage": true,
};
export const ALL_PERMISSIONS = Object.keys(PERMISSION_REGISTRY);
// Cross-tenant platform administration. Only `User.isPlatformAdmin` grants
// these (PermissionResolverService): a tenant Role or an API key that holds
// one — written before the role DTOs refused it, or straight into the
// database — grants nothing.
export const PLATFORM_ONLY_PERMISSIONS = [
    "tenants.manage",
];
const PLATFORM_ONLY_SET = new Set(PLATFORM_ONLY_PERMISSIONS);
export function isPlatformOnlyPermission(p) {
    return PLATFORM_ONLY_SET.has(p);
}
// Everything a tenant Role or an API key may hold: the role DTOs validate
// against this list, and the role editor's permission matrix shows it.
export const TENANT_ASSIGNABLE_PERMISSIONS = ALL_PERMISSIONS.filter((p) => !isPlatformOnlyPermission(p));
// Ceiling assigned to a freshly-minted API key (auth/api-key.controller.ts):
// the operational inventory actions integrations actually automate, never
// `users.manage` or any interactive admin-console-only permission
// (accounting config, tenant settings, fiscal year close, purchase-invoice
// approval, ...). Stored on `ApiKey.permissions` at creation; effective
// scope on each request is the intersection of that stored list with
// whatever the owning user currently holds
// (PermissionResolverService.resolveForApiKey), so a demoted owner
// immediately narrows every key they've minted.
export const DEFAULT_API_KEY_PERMISSIONS = [
    "inventory.editCost",
    "inventory.movements.receive",
    "inventory.movements.transfer",
    "inventory.movements.consume",
    "inventory.movements.adjust",
    // MollieController's payment-taking routes (create/status/qr/cancel,
    // terminal listing) explicitly document `@ApiSecurity('api-key')` support
    // — an existing, deliberate integration surface.
    "payments.create",
];
// Every permission a Bonnie tool offered on the MCP endpoint (channel `mcp`
// in the backend's TOOL_ACCESS) needs: what the "Claude (MCP)" key preset
// grants, together with `bonnie.use`. A key carries these permissions to
// every REST route that accepts an API key, not only to Claude's tools, so
// the list holds no administrative permission (users, roles,
// administrations, tenant settings, API keys, tenants). The one exception
// is `integrations.whatsapp.manage`, for get_conversation_history: every
// REST route requiring it is JWT-only (WhatsAppController's JwtAuthGuard),
// so a key holding it reaches the WhatsApp inbox only through that tool.
// The backend spec `mcp-tool-permissions.spec.ts` holds this list equal to
// TOOL_ACCESS (a new MCP tool fails it until the tool's permissions are
// added here) and free of the administrative ones;
// `whatsapp-routes-jwt-only.spec.ts` pins the WhatsApp routes to JWT.
export const MCP_TOOL_PERMISSIONS = [
    "accounts.read",
    "accounts.update",
    "integrations.whatsapp.manage",
    "inventory.stock.read",
    "invoices.read",
    "invoices.send",
    "items.read",
    "items.update",
    "orders.create",
    "orders.read",
    "orders.update",
    "payments.create",
    "reports.read",
];
export function hasPermission(perms, p) {
    return perms !== undefined && perms.includes(p);
}
